import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, clearAdminToken, hasAdminAuth, clearAdminMe } from '../lib/adminApi';
import { useToast } from '../hooks/use-toast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { AnalyticsPanel, ImportPanel, LeadRulesPanel, AuditLogsPanel } from './AdminPhase4Panels';
import SiteSettingsPanel from './SiteSettingsPanel';
import AdminPagesPanel from './AdminPagesPanel';
import AdminCertificatesPanel from './AdminCertificatesPanel';
import AdminUsersPanel from '../components/AdminUsersPanel';
import AdminPaymentsPanel from '../components/AdminPaymentsPanel';
import AdminPaymentEventsPanel from '../components/AdminPaymentEventsPanel';
import AdminBackupsPanel from '../components/AdminBackupsPanel';
import AdminRequirementsPanel from '../components/AdminRequirementsPanel';
import AdminTopBar from '../components/admin/AdminTopBar';
import AdminSidebar from '../components/admin/AdminSidebar';
import AdminOverview from '../components/admin/AdminOverview';
import AdminDataTable from '../components/admin/AdminDataTable';
import AdminRecordForm from '../components/admin/AdminRecordForm';
import AdminVendorForm from '../components/admin/AdminVendorForm';
import AdminProductForm from '../components/admin/AdminProductForm';
import { FLAT_TABS } from '../components/admin/adminTabs';
import { adminAuthHeaders } from '../components/admin/adminHelpers';

/**
 * Coerce any error (axios / fetch / Pydantic 422) into a flat string so it
 * can safely render inside a Sonner toast. Pydantic v2 returns 422 with
 * `detail` as an array of `{type, loc, msg, input, url}` objects — dropping
 * that array straight into a toast description crashes React with
 * "Objects are not valid as a React child".
 */
const formatApiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const path = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
        return path ? `${path}: ${d?.msg || d?.type || ''}` : (d?.msg || d?.type || JSON.stringify(d));
      })
      .join('; ');
  }
  if (typeof detail === 'string') return detail;
  return err?.message || 'Request failed';
};

// Build name-based dropdown options (id=name so the stored value is the name).
// Active rows sorted by order/name, plus the record's current value when it's a
// legacy entry not in the catalogue (so editing never silently blanks it out).
const buildNameOptions = (list, currentValue) => {
  const arr = Array.isArray(list) ? list : [];
  let opts = arr
    .filter((c) => c.is_active !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0) || (a.name || '').localeCompare(b.name || ''))
    .map((c) => ({ id: c.name, name: c.name, slug: '' }));
  if (currentValue && !opts.some((o) => o.id === currentValue)) {
    opts = [{ id: currentValue, name: currentValue, slug: 'current' }, ...opts];
  }
  return opts;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState({ manage_category: true });
  const [page, setPage] = useState(1);
  // Server-side pagination state — currently used only for the Vendors (suppliers) tab
  // which holds 20 000+ rows. Other tabs stay on client-side pagination.
  const [pageSize, setPageSize] = useState(50);
  const [suppliersTotal, setSuppliersTotal] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    // Verify the httpOnly cookie session via /auth/me. If 401, redirect to login.
    let alive = true;
    (async () => {
      if (!hasAdminAuth()) {
        navigate('/admin/login', { replace: true });
        return;
      }
      try {
        await adminApi.authMe();
      } catch (err) {
        if (!alive) return;
        clearAdminToken();
        clearAdminMe();
        navigate('/admin/login', { replace: true });
      }
    })();
    return () => { alive = false; };
  }, [navigate]);

  const tab = FLAT_TABS.find((t) => t.key === activeTab);

  const fetchData = async (key) => {
    setLoading(true);
    try {
      let res = [];
      if (key === 'overview') {
        const [mc, c, sc, p, sPage, e, r, cust] = await Promise.all([
          adminApi.list('main_categories'),
          adminApi.list('categories'),
          adminApi.list('sub_categories'),
          adminApi.list('products'),
          // Suppliers KPI uses total from the paginated endpoint (avoids downloading
          // 2 000+ vendor rows just for a count).
          adminApi.listSuppliersPaginated({ limit: 1 }).catch(() => ({ total: 0 })),
          adminApi.enquiries().catch(() => []),
          adminApi.requirements().catch(() => []),
          adminApi.list('customers').catch(() => []),
        ]);
        setData({
          ...data,
          overview: {
            mc, c, sc, p,
            s: { length: sPage?.total || 0 },
            e, r, cust,
          },
        });
      } else if (key === 'suppliers') {
        const skip = (page - 1) * pageSize;
        const resp = await adminApi.listSuppliersPaginated({
          limit: pageSize,
          skip,
          q: debouncedSearch,
        });
        setSuppliersTotal(resp?.total || 0);
        setData({ ...data, suppliers: Array.isArray(resp?.items) ? resp.items : [] });
      } else if (key === 'enquiries') {
        res = await adminApi.enquiries();
        setData({ ...data, [key]: res });
      } else if (key === 'requirements') {
        res = await adminApi.requirements();
        setData({ ...data, [key]: res });
      } else {
        const t = FLAT_TABS.find((tt) => tt.key === key);
        res = await adminApi.list(t.resource);
        const extras = {};
        if (key === 'categories' || key === 'sub_categories') {
          if (!Array.isArray(data.main_categories)) {
            extras.main_categories = await adminApi.list('main_categories').catch(() => []);
          }
        }
        if (key === 'sub_categories') {
          if (!Array.isArray(data.categories)) {
            extras.categories = await adminApi.list('categories').catch(() => []);
          }
        }
        if (key === 'customers') {
          if (!Array.isArray(data.cities)) {
            extras.cities = await adminApi.list('cities').catch(() => []);
          }
          if (!Array.isArray(data.districts)) {
            extras.districts = await adminApi.list('districts').catch(() => []);
          }
        }
        if (key === 'cities') {
          if (!Array.isArray(data.districts)) {
            extras.districts = await adminApi.list('districts').catch(() => []);
          }
        }
        setData({ ...data, ...extras, [key]: res });
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        toast({ title: 'Session expired', description: 'Please sign in again.', variant: 'destructive' });
        clearAdminToken();
        clearAdminMe();
        navigate('/admin/login');
        return;
      }
      toast({ title: 'Failed to load', description: formatApiError(err), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = FLAT_TABS.find((tt) => tt.key === activeTab);
    if (t?.custom) { setPage(1); return; }
    fetchData(activeTab); setPage(1);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [activeTab]);

  // Suppliers tab is server-paginated: refetch whenever page / pageSize /
  // debounced search changes.
  useEffect(() => {
    if (activeTab !== 'suppliers') return;
    fetchData('suppliers');
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [page, pageSize, debouncedSearch, activeTab]);

  // Debounce the search input (300 ms) — server-paginated tabs use the
  // debounced value; client-paginated tabs continue to filter on every
  // keystroke against the already-loaded data.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Reset to page 1 whenever a new server-side search term or page size is
  // applied so the user doesn't land on an empty page.
  useEffect(() => {
    if (activeTab === 'suppliers') setPage(1);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [debouncedSearch, pageSize]);

  const logout = async () => {
    try { await adminApi.logout(); } catch (err) {
      console.warn('[Admin] logout API failed; clearing local state:', err?.message || err);
    }
    clearAdminToken();
    clearAdminMe();
    navigate('/admin/login');
  };

  const handleSave = async (formValues) => {
    try {
      const isUpdate = editing && editing.id;

      // Vendors use a richer multipart endpoint
      if (tab.key === 'suppliers') {
        const fd = new FormData();
        const v = formValues || {};
        fd.append('company_name', v.name || v.company_name || '');
        fd.append('contact_name', v.contact_name || '');
        fd.append('mobile', v.mobile || '');
        fd.append('city', v.city || '');
        fd.append('district', v.district || '');
        fd.append('email', v.email || '');
        fd.append('website', v.website || '');
        fd.append('gst_number', v.gst_number || '');
        fd.append('year_established', String(Number(v.year_established || v.established || 0)));
        fd.append('business_type', v.business_type || '');
        fd.append('seller_type', v.seller_type || '');
        fd.append('categories', Array.isArray(v.categories) ? v.categories.join(',') : (v.categories || ''));
        fd.append('description', v.description || '');
        fd.append('status', v.status || 'active');
        if (v.business_hours) fd.append('business_hours', JSON.stringify(v.business_hours));
        if (v._companyDoc) fd.append('company_doc', v._companyDoc);
        if (v._gstDoc) fd.append('gst_doc', v._gstDoc);
        if (v._logoImage) fd.append('logo_image', v._logoImage);

        const url = isUpdate
          ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/vendors/${editing.id}/full`
          : `${process.env.REACT_APP_BACKEND_URL}/api/admin/vendors/full`;
        const res = await fetch(url, {
          method: isUpdate ? 'PUT' : 'POST',
          headers: adminAuthHeaders(),
          credentials: 'include',
          body: fd,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `HTTP ${res.status}`);
        }
        const saved = await res.json();
        // Assign plan if changed
        const newPlanId = v.plan_id || null;
        const currentPlanId = editing?.plan_id || null;
        if (newPlanId !== currentPlanId) {
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/vendors/${saved.id || editing.id}/plan`, {
            method: 'PUT',
            headers: adminAuthHeaders({ 'Content-Type': 'application/json' }),
            credentials: 'include',
            body: JSON.stringify({ plan_id: newPlanId }),
          });
        }
        toast({ title: isUpdate ? 'Vendor updated' : 'Vendor created' });
        setEditing(null);
        fetchData(activeTab);
        return;
      }

      const payload = { ...formValues };
      // Drop empty numeric fields so Pydantic falls back to model defaults
      // instead of trying to coerce "" → int and 422'ing the request. This
      // matters for catalogues synced from prod that don't have `order` set.
      ['price', 'order', 'rating', 'established'].forEach((k) => {
        if (payload[k] === '' || payload[k] === null || payload[k] === undefined) {
          delete payload[k];
        } else {
          payload[k] = Number(payload[k]);
        }
      });
      if (tab.key === 'products') {
        payload.images = payload.image ? [payload.image] : [];
        payload.specs = payload.specs || {};
      }
      if (isUpdate) {
        await adminApi.update(tab.resource, editing.id, payload);
        toast({ title: 'Updated' });
      } else {
        await adminApi.create(tab.resource, payload);
        toast({ title: 'Created' });
      }
      setEditing(null);
      fetchData(activeTab);
    } catch (err) {
      toast({ title: 'Save failed', description: formatApiError(err), variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item? This cannot be undone.')) return;
    try {
      await adminApi.remove(tab.resource, id);
      toast({ title: 'Deleted' });
      fetchData(activeTab);
    } catch (err) {
      toast({ title: 'Delete failed', description: formatApiError(err), variant: 'destructive' });
    }
  };

  const handleVendorApprove = async (id, status) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/vendors/${id}/status`, {
        method: 'PUT',
        headers: adminAuthHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify({ status }),
      }).then((r) => { if (!r.ok) throw new Error(r.statusText); });
      toast({ title: `Vendor ${status === 'active' ? 'activated' : 'deactivated'}` });
      fetchData(activeTab);
    } catch (err) {
      toast({ title: 'Update failed', description: formatApiError(err), variant: 'destructive' });
    }
  };

  const handleCustomerStatus = async (id, status) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/${id}/status`, {
        method: 'PUT',
        headers: adminAuthHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || res.statusText);
      }
      toast({ title: `Customer ${status === 'active' ? 'activated' : 'deactivated'}` });
      fetchData(activeTab);
    } catch (err) {
      toast({ title: 'Update failed', description: formatApiError(err), variant: 'destructive' });
    }
  };

  const handleImpersonateVendor = async (vendor) => {
    if (!window.confirm(`Login as vendor "${vendor.name}"? You will be opened into their dashboard.`)) return;
    try {
      const r = await adminApi.impersonateVendor(vendor.id);
      // 1. Stash legacy bearer for child panels that still send Authorization headers.
      localStorage.setItem('tgmart_vendor_token', r.token);
      // 2. CRITICAL: cache the vendor profile so VendorDashboard.isAuthed flips true
      //    on mount. Without this the dashboard sees missing `tgmart_vendor` and
      //    renders the OTP login screen — even though the httpOnly tgm_vendor_jwt
      //    cookie was set by the impersonation response. (Bug fix 2026-02-20.)
      if (r.vendor) localStorage.setItem('tgmart_vendor', JSON.stringify(r.vendor));
      localStorage.setItem('tgmart_impersonating', JSON.stringify({
        role: 'vendor', id: vendor.id, name: vendor.name, at: new Date().toISOString(),
      }));
      // Notify the header / session hooks so any open tabs flip into vendor view immediately.
      window.dispatchEvent(new Event('tgmart-session-change'));
      toast({ title: `Logged in as ${vendor.name}`, description: 'Opening vendor dashboard…' });
      window.open('/vendor/dashboard', '_blank');
    } catch (err) {
      toast({ title: 'Impersonation failed', description: formatApiError(err), variant: 'destructive' });
    }
  };

  const handleImpersonateCustomer = async (customer) => {
    const label = customer.name || customer.mobile || customer.id;
    if (!window.confirm(`Login as customer "${label}"? You will be opened into their account.`)) return;
    try {
      const r = await adminApi.impersonateCustomer(customer.id);
      localStorage.setItem('tgmart_token', r.token);
      localStorage.setItem('tgmart_user', JSON.stringify(r.user));
      localStorage.setItem('tgmart_impersonating', JSON.stringify({
        role: 'customer', id: customer.id, name: label, at: new Date().toISOString(),
      }));
      toast({ title: `Logged in as ${label}`, description: 'Opening account…' });
      window.open('/account', '_blank');
    } catch (err) {
      toast({ title: 'Impersonation failed', description: formatApiError(err), variant: 'destructive' });
    }
  };

  const handleSendWelcome = async (resource, item) => {
    const label = item.name || item.mobile || item.id;
    if (!item.email) {
      toast({ title: 'No email on file', description: `${label} has no email — welcome cannot be sent.`, variant: 'destructive' });
      return;
    }
    if (!window.confirm(`Send welcome email to ${label} (${item.email})?`)) return;
    try {
      const fn = resource === 'suppliers' ? adminApi.sendWelcomeVendor : adminApi.sendWelcomeCustomer;
      await fn(item.id);
      toast({ title: 'Welcome email sent', description: `${item.email}` });
    } catch (err) {
      toast({ title: 'Send failed', description: formatApiError(err), variant: 'destructive' });
    }
  };

  const handleSendWelcomeBulk = async (resource, ids, onDone) => {
    try {
      // Backend routes live under /vendors, while the admin tab key is 'suppliers'.
      const r = await adminApi.sendWelcomeBulk(resource === 'suppliers' ? 'vendors' : resource, ids);
      const desc = `${r.sent} sent, ${r.skipped} skipped${r.errors?.length ? ` (${r.errors.length} errors)` : ''}.`;
      toast({ title: 'Bulk welcome complete', description: desc });
      onDone?.();
    } catch (err) {
      toast({ title: 'Bulk send failed', description: formatApiError(err), variant: 'destructive' });
    }
  };

  const handleBulkDelete = async (resource, ids, onDone) => {
    try {
      const r = await adminApi.bulkDelete(resource, ids);
      toast({ title: 'Bulk delete complete', description: `${r.deleted} record(s) deleted.` });
      onDone?.();
      fetchData(activeTab);
    } catch (err) {
      toast({ title: 'Bulk delete failed', description: formatApiError(err), variant: 'destructive' });
    }
  };

  const handleBulkCustomerStatus = async (ids, status, onDone) => {
    try {
      const r = await adminApi.bulkCustomerStatus(ids, status);
      const verb = status === 'active' ? 'activated' : 'deactivated';
      toast({ title: `${r.modified} customer(s) ${verb}`, description: `${r.requested} requested · ${r.matched} matched.` });
      onDone?.();
      fetchData(activeTab);
    } catch (err) {
      toast({ title: 'Bulk status update failed', description: formatApiError(err), variant: 'destructive' });
    }
  };

  const selectTab = (key) => { setActiveTab(key); setSearch(''); };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminTopBar onLogout={logout} />

      <div className="grid grid-cols-[220px_1fr] min-h-[calc(100vh-3.5rem)]">
        <AdminSidebar
          activeTab={activeTab}
          onSelect={selectTab}
          openGroups={openGroups}
          setOpenGroups={setOpenGroups}
        />

        <main className="p-6">
          {activeTab === 'analytics' && <AnalyticsPanel />}
          {activeTab === 'import' && <ImportPanel />}
          {activeTab === 'lead_rules' && <LeadRulesPanel />}
          {activeTab === 'audit_logs' && <AuditLogsPanel />}
          {activeTab === 'certificates' && <AdminCertificatesPanel />}
          {activeTab === 'pages' && <AdminPagesPanel />}
          {activeTab === 'admin_users' && <AdminUsersPanel />}
          {activeTab === 'payments' && <AdminPaymentsPanel />}
          {activeTab === 'payment_events' && <AdminPaymentEventsPanel />}
          {activeTab === 'backups' && <AdminBackupsPanel />}
          {activeTab === 'requirements' && <AdminRequirementsPanel />}
          {activeTab === 'site_settings' && <SiteSettingsPanel />}

          {activeTab === 'overview' && <AdminOverview overview={data.overview} />}

          {activeTab !== 'overview' && !tab?.custom && (
            <AdminDataTable
              tab={tab}
              activeTab={activeTab}
              data={data}
              loading={loading}
              search={search}
              setSearch={setSearch}
              page={page}
              setPage={setPage}
              setEditing={setEditing}
              onDelete={handleDelete}
              onVendorApprove={handleVendorApprove}
              onCustomerStatus={handleCustomerStatus}
              onImpersonateVendor={handleImpersonateVendor}
              onImpersonateCustomer={handleImpersonateCustomer}
              onSendWelcome={handleSendWelcome}
              onSendWelcomeBulk={handleSendWelcomeBulk}
              onBulkDelete={handleBulkDelete}
              onBulkCustomerStatus={handleBulkCustomerStatus}
              serverPaginated={activeTab === 'suppliers'}
              totalRecords={activeTab === 'suppliers' ? suppliersTotal : undefined}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          )}
        </main>
      </div>

      {/* Edit / Create modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit' : 'Create'} {tab?.label?.replace(/s$/, '') || ''}</DialogTitle>
            <DialogDescription>Fill the fields below and save.</DialogDescription>
          </DialogHeader>
          {editing && tab?.key === 'suppliers' && (
            <AdminVendorForm initial={editing} onSave={handleSave} onCancel={() => setEditing(null)} />
          )}
          {editing && tab?.key === 'products' && (
            <AdminProductForm initial={editing} onSave={handleSave} onCancel={() => setEditing(null)} />
          )}
          {editing && tab?.key !== 'suppliers' && tab?.key !== 'products' && tab?.fields && (
            <AdminRecordForm
              fields={tab.fields}
              initial={editing}
              onSave={handleSave}
              onCancel={() => setEditing(null)}
              resource={tab.resource}
              parentOptions={{
                main_category_id: Array.isArray(data['main_categories']) ? data['main_categories'] : [],
                category_id: Array.isArray(data['categories']) ? data['categories'] : [],
                city: buildNameOptions(data['cities'], editing?.city),
                district: buildNameOptions(data['districts'], editing?.district),
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
